if (!customElements.get('gt-product-gallery')) {
  customElements.define(
    'gt-product-gallery',
    class GtProductGallery extends HTMLElement {
      constructor() {
        super();
        this.onThumbClick = this.onThumbClick.bind(this);
        this.onThumbNavClick = this.onThumbNavClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onBuyNowClick = this.onBuyNowClick.bind(this);
        this.onGalleryScroll = this.onGalleryScroll.bind(this);
        this.onMediaQueryChange = this.onMediaQueryChange.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.root = this.closest('.gt-pdp') || document;
        if (!this.root) return;

        this.mobileQuery = window.matchMedia('(max-width: 989px)');
        this.galleryScroller = this.root.querySelector('[data-gallery-main]');
        this.galleryProgress = this.root.querySelector('[data-gallery-progress]');
        this.galleryProgressBar = this.root.querySelector('[data-gallery-progress-bar]');

        this.bindGalleryRefs();

        // Lắng nghe sự kiện click Thumb
        this.thumbButtons.forEach((btn) => btn.addEventListener('click', this.onThumbClick));
        this.querySelectorAll('[data-thumb-scroll]').forEach((btn) =>
          btn.addEventListener('click', this.onThumbNavClick)
        );

        this.galleryScroller?.addEventListener('scroll', this.onGalleryScroll, { passive: true });
        this.mobileQuery.addEventListener('change', this.onMediaQueryChange);

        // Khởi tạo item Active ban đầu
        this.initActiveItem();

        // 1. Lắng nghe qua PubSub (Dawn standard)
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        // 2. Fallback: Lắng nghe change event trực tiếp từ Variant inputs
        this.onNativeVariantChange = (e) => {
          if (e.target.closest('variant-selects, variant-radios, [data-single-option-selector]')) {
            setTimeout(() => this.onVariantChange(e), 60);
          }
        };
        this.root.addEventListener('change', this.onNativeVariantChange);

        // Re-init khi product-info load lại DOM
        this.root.addEventListener('product-info:loaded', () => this.reinit());
        this.initBuyNow();
      }

      disconnectedCallback() {
        this.variantUnsubscriber?.();
        if (this.onNativeVariantChange) {
          this.root?.removeEventListener('change', this.onNativeVariantChange);
        }
        this.thumbButtons?.forEach((btn) => btn.removeEventListener('click', this.onThumbClick));
        this.galleryScroller?.removeEventListener('scroll', this.onGalleryScroll);
        this.mobileQuery?.removeEventListener('change', this.onMediaQueryChange);
      }

      isMobileGallery() {
        return this.mobileQuery?.matches ?? window.matchMedia('(max-width: 989px)').matches;
      }

      bindGalleryRefs() {
        this.thumbsScroller = this.querySelector('[data-thumbs-scroller]');
        this.thumbButtons = this.querySelectorAll('.gt-pdp__thumb');
        this.galleryItems = this.root.querySelectorAll('.gt-pdp__gallery-item[data-media-id]');
      }

      getAllGalleryItems() {
        this.galleryItems = this.root.querySelectorAll('.gt-pdp__gallery-item[data-media-id]');
        return Array.from(this.galleryItems || []);
      }

      initActiveItem() {
        const items = this.getAllGalleryItems();
        if (!items.length) return;

        const currentActive = this.root.querySelector('.gt-pdp__gallery-item.is-active');
        let mediaId = currentActive?.dataset?.mediaId || items[0]?.dataset?.mediaId;

        if (mediaId) {
          this.setActiveGalleryItem(mediaId);
          this.setActiveThumb(mediaId);
          this.updateGalleryProgress(mediaId);
        }
      }

      reinit() {
        this.bindGalleryRefs();
        this.thumbButtons.forEach((btn) => btn.addEventListener('click', this.onThumbClick));
        this.initActiveItem();
        this.initBuyNow();
      }

      onMediaQueryChange() {
        this.updateGalleryProgress();
      }

      updateGalleryProgress(mediaId) {
        if (!this.galleryProgressBar) return;

        const items = this.getAllGalleryItems();
        if (this.galleryProgress) {
          this.galleryProgress.classList.toggle(
            'is-hidden',
            items.length <= 1 || !this.isMobileGallery()
          );
        }

        if (!this.isMobileGallery()) return;

        const total = items.length;
        if (total <= 0) return;

        let index = 0;
        if (mediaId) {
          const found = items.findIndex((item) => String(item.dataset.mediaId) === String(mediaId));
          if (found >= 0) index = found;
        }

        const percent = Math.round(((index + 1) / total) * 100);
        this.galleryProgressBar.style.width = `${percent}%`;
        this.galleryProgress?.setAttribute('aria-valuenow', String(percent));
      }

      onGalleryScroll() {
        if (!this.isMobileGallery() || !this.galleryScroller) return;

        const maxScroll = this.galleryScroller.scrollWidth - this.galleryScroller.clientWidth;
        if (maxScroll > 0) {
          const scrollPercent = Math.min(100, Math.round((this.galleryScroller.scrollLeft / maxScroll) * 100));
          this.galleryProgressBar.style.width = `${scrollPercent}%`;
          this.galleryProgress?.setAttribute('aria-valuenow', String(scrollPercent));
        }
      }

      setActiveThumb(mediaId) {
        if (!mediaId) return;
        const items = this.querySelectorAll('.gt-pdp__thumbs-item');
        let activeItem = null;

        items.forEach((item) => {
          const isActive = String(item.dataset.mediaId) === String(mediaId);
          item.classList.toggle('is-active', isActive);
          const btn = item.querySelector('.gt-pdp__thumb');
          if (btn) btn.setAttribute('aria-current', isActive ? 'true' : 'false');
          if (isActive) activeItem = item;
        });

        if (activeItem && this.thumbsScroller && !this.isMobileGallery()) {
          activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }

      setActiveGalleryItem(mediaId) {
        const items = this.getAllGalleryItems();
        if (!items.length) return;

        let hasMatch = false;
        let targetItem = null;

        // 1. Thử match theo mediaId
        if (mediaId) {
          items.forEach((item) => {
            const isActive = String(item.dataset.mediaId) === String(mediaId);
            item.classList.toggle('is-active', isActive);
            if (isActive) {
              hasMatch = true;
              targetItem = item;
            }
          });
        }

        // 2. Safety Fallback: Nếu variant không gán ảnh riêng, bắt buộc active ảnh đầu tiên
        if (!hasMatch) {
          items.forEach((item, idx) => {
            const isActive = idx === 0;
            item.classList.toggle('is-active', isActive);
            if (isActive) targetItem = item;
          });
        }

        if (targetItem) {
          this.scrollToGalleryItem(targetItem);
        }
      }

      scrollToGalleryItem(el) {
        if (!el) return;
        if (this.isMobileGallery()) {
          el.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
          return;
        }
        const top = el.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
        window.scrollTo({ top, behavior: 'smooth' });
      }

      onThumbClick(event) {
        const btn = event.currentTarget;
        const targetId = btn.dataset.target;
        let el = document.getElementById(targetId);

        // Fallback nếu không tìm thấy qua ID
        if (!el) {
          const thumbItem = btn.closest('.gt-pdp__thumbs-item');
          const mediaId = thumbItem?.dataset?.mediaId;
          if (mediaId) {
            el = this.root.querySelector(`.gt-pdp__gallery-item[data-media-id="${mediaId}"]`);
          }
        }

        if (!el) return;

        const mediaId = el.dataset.mediaId;
        this.setActiveGalleryItem(mediaId);
        this.setActiveThumb(mediaId);
        this.updateGalleryProgress(mediaId);
      }

      onThumbNavClick(event) {
        if (!this.thumbsScroller) return;

        const direction = event.currentTarget.dataset.thumbScroll;
        const firstThumb = this.thumbsScroller.querySelector('.gt-pdp__thumbs-item');
        if (!firstThumb) return;

        const thumbWidth = firstThumb.offsetWidth;
        const listStyle = window.getComputedStyle(this.thumbsScroller.querySelector('.gt-pdp__thumbs-list'));
        const gap = parseFloat(listStyle.gap) || 12;

        const scrollAmount = thumbWidth + gap;

        this.thumbsScroller.scrollBy({
          left: direction === 'up' ? -scrollAmount : scrollAmount,
          behavior: 'smooth'
        });
      }

      getScrollOffset() {
        const headerHeight = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height')
        );
        return (Number.isNaN(headerHeight) ? 0 : headerHeight) + 16;
      }

      onVariantChange(event) {
        let mediaId = null;

        // 1. Lấy variant object từ các nguồn Payload khác nhau của Shopify
        const variant = 
          event?.data?.variant || 
          event?.detail?.variant || 
          event?.variant;

        if (variant && variant.featured_media) {
          mediaId = String(variant.featured_media.id);
        } else {
          // 2. Fallback: Nếu event không mang theo variant object,
          // đọc thẳng dữ liệu JSON biến thể từ element <variant-selects> hoặc <variant-radios>
          const variantContainer = this.root.querySelector('variant-selects, variant-radios');
          if (variantContainer && typeof variantContainer.getVariantData === 'function') {
            const currentVariant = variantContainer.currentVariant;
            if (currentVariant && currentVariant.featured_media) {
              mediaId = String(currentVariant.featured_media.id);
            }
          }
        }

        // 3. Nếu tìm thấy mediaId chuẩn từ Variant, mới tiến hành update Active
        if (mediaId) {
          this.setActiveGalleryItem(mediaId);
          this.setActiveThumb(mediaId);
          this.updateGalleryProgress(mediaId);
        }
      }

      initBuyNow() {
        const buyNowBtn = this.root.querySelector('[data-buy-now]');
        if (!buyNowBtn || buyNowBtn.dataset.buyNowBound === 'true') return;
        buyNowBtn.dataset.buyNowBound = 'true';
        buyNowBtn.addEventListener('click', this.onBuyNowClick);
      }

      onBuyNowClick(event) {
        const btn = event.currentTarget;
        if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

        const form = this.root.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const formData = new FormData(form);
        const variantId = formData.get('id');
        if (!variantId) return;

        btn.classList.add('loading');
        btn.setAttribute('aria-disabled', 'true');
        const spinner = btn.querySelector('.loading__spinner');
        spinner?.classList.remove('hidden');

        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { Accept: 'application/javascript' } };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(`${window.routes?.cart_add_url || '/cart/add'}`, config)
          .then((res) => res.json())
          .then((response) => {
            if (response.status) {
              const productForm = this.root.querySelector('product-form');
              productForm?.handleErrorMessage?.(response.description);
              return;
            }
            window.location.href = '/checkout';
          })
          .catch((err) => console.error(err))
          .finally(() => {
            btn.classList.remove('loading');
            btn.removeAttribute('aria-disabled');
            spinner?.classList.add('hidden');
          });
      }
    }
  );
}

// Bắt sự kiện Share Copy
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-share-copy]').forEach((btn) => {
    if (btn.dataset.shareCopyBound === 'true') return;
    btn.dataset.shareCopyBound = 'true';
    btn.addEventListener('click', async () => {
      const url = btn.dataset.shareUrl;
      const message = btn.closest('.gt-pdp__share')?.querySelector('[data-share-message]');
      try {
        await navigator.clipboard.writeText(url);
        if (message) {
          message.textContent = 'Link copied';
          message.classList.remove('visually-hidden');
          setTimeout(() => {
            message.textContent = '';
            message.classList.add('visually-hidden');
          }, 2000);
        }
      } catch (e) {
        console.error(e);
      }
    });
  });
});